import { WebFormQuestionType } from "@/content-usage/__generated__/ContentModulesDragDropProvider_UpdateContentUsageMutation.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import MembersProfileFieldFilterOption, {
  MembersProfileFieldFilterOptionProps,
  ProfileFieldFilterOption,
} from "@/organization/people/page/filters/MembersProfileFieldFilterOption"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import { MembersProfileFieldFilterQuery } from "@components/dropdown/profile-field/__generated__/MembersProfileFieldFilterQuery.graphql"
import { DiscoText, useDropdownContext } from "@disco-ui"
import DiscoPaginatedDropdownContent, {
  DiscoPaginatedDropdownContentSkeleton,
} from "@disco-ui/dropdown/DiscoPaginatedDropdownContent"
import { TestIDProps } from "@utils/typeUtils"
import { graphql } from "react-relay"

const PROFILE_FIELDS_PER_LOAD = 10

export type MembersProfileFieldFilterState = {
  profileFields: Record<GlobalID, ProfileFieldFilterOption | null>
}

export type MembersProfileFieldFilterProps = TestIDProps & {
  filterState: MembersProfileFieldFilterState
  setFilterState: (state: MembersProfileFieldFilterState) => void
  showDefaultOptions: MembersProfileFieldFilterOptionProps["showDefaultOptions"]
  isPrivate?: boolean | null
  webFormQuestionTypes?: WebFormQuestionType[] | null
}

function MembersProfileFieldFilter({
  testid = "MembersProfileFieldFilter",
  filterState,
  setFilterState,
  showDefaultOptions,
  isPrivate = null,
  webFormQuestionTypes = null,
}: MembersProfileFieldFilterProps) {
  const activeOrganization = useActiveOrganization()
  const dropdown = useDropdownContext()

  const { data, pagination } =
    Relay.useRefetchablePaginationQuery<MembersProfileFieldFilterQuery>(
      graphql`
        query MembersProfileFieldFilterQuery(
          $id: ID!
          $first: Int!
          $search: String
          $after: String
          $isPrivate: Boolean
          $webFormQuestionTypes: [WebFormQuestionType!]
        ) {
          organization: node(id: $id) {
            ... on Organization {
              profileFieldsForFiltering: profileFields(
                first: $first
                after: $after
                search: $search
                status: visible
                defaultTypesToExclude: [email_address, public_groups]
                isPrivate: $isPrivate
                webFormQuestionTypes: $webFormQuestionTypes
              ) @connection(key: "MembersProfileFieldFilter__profileFieldsForFiltering") {
                edges {
                  node {
                    id
                    title
                    ...MembersProfileFieldFilterOptionFragment
                  }
                }
                pageInfo {
                  startCursor
                  endCursor
                  hasNextPage
                  hasPreviousPage
                }
              }
            }
          }
        }
      `,
      {
        id: activeOrganization?.id,
        first: PROFILE_FIELDS_PER_LOAD,
        isPrivate,
        webFormQuestionTypes,
      },
      {
        connectionName: "organization.profileFieldsForFiltering",
        fetchPolicy: "network-only",
      }
    )

  const organization = data?.organization
  if (!organization) return null

  const profileFields = Relay.connectionToArray(organization.profileFieldsForFiltering)

  return (
    <DiscoPaginatedDropdownContent testid={testid} pagination={pagination}>
      {profileFields.length ? (
        profileFields.map((profileField) => {
          if (profileField.title === "Email Address") return null
          return (
            <MembersProfileFieldFilterOption
              key={profileField.id}
              profileFieldKey={profileField}
              filterState={filterState}
              setFilterState={setFilterState}
              onClose={handleClose}
              showDefaultOptions={showDefaultOptions}
            />
          )
        })
      ) : (
        <DiscoText color={"text.secondary"} variant={"body-sm-500"} align={"center"}>
          {"No results"}
        </DiscoText>
      )}
    </DiscoPaginatedDropdownContent>
  )

  function handleClose(event: React.MouseEvent<HTMLElement>) {
    dropdown.closeParent(event)
  }
}

const MembersProfileFieldFilterSkeleton = () => {
  return <DiscoPaginatedDropdownContentSkeleton />
}

export default Relay.withSkeleton({
  component: MembersProfileFieldFilter,
  skeleton: MembersProfileFieldFilterSkeleton,
})
